import {
  MessageDotsIcon,
  MessageIcon,
  RightArrowIcon
} from '@/components/common/icon';
import { useTimModalStore } from '@/tim/useTimModalStore';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { getDetailOrderId } from '@/utils/getDetailOrderId';

const formatUnreadCount = (count: number) => (count > 99 ? '99+' : count);

const UnreadCountBadge = ({
  count,
  className
}: {
  count: number;
  className?: string;
}) => {
  if (count <= 0) return null;

  return (
    <span
      className={`pointer-events-none flex h-[22px] min-w-[22px] items-center justify-center whitespace-nowrap rounded-full bg-[var(--creator-sidebar-unread)] px-1 font-['DM_Sans'] text-sm font-semibold leading-5 text-white ${className || ''}`}
    >
      {formatUnreadCount(count)}
    </span>
  );
};

export default function MessageButton({
  order,
  timUnreadCount,
  className,
  isModule = false,
  isText = false,
  isIcon = false,
  showIcon = true,
  label,
  icon
}: {
  order: any;
  timUnreadCount: number;
  className?: string;
  isModule?: boolean;
  isText?: boolean;
  isIcon?: boolean;
  showIcon?: boolean;
  label?: ReactNode;
  icon?: ReactNode;
}) {
  const { t } = useTranslation();
  const { setShowTimModal } = useTimModalStore();
  const orderId = getDetailOrderId(order);

  const handleMessage = () => {
    if (!orderId) return;
    setShowTimModal(true, {
      campaignId: order?.campaignId,
      orderId
    });
  };

  if (isIcon) {
    return (
      <div
        className={`h-6 flex items-center justify-center cursor-pointer flex-shrink-0 gap-1 ${className || ''}`}
        onClick={handleMessage}
      >
        <div className="h-4 border-l border-[#E7E5E4]"></div>
        <div
          className="relative flex h-6 w-6 items-center justify-center"
          data-testid="message-button-icon"
        >
          <MessageDotsIcon className="w-6 h-6 text-[#A9A29D]" />
          <UnreadCountBadge
            count={timUnreadCount}
            className="absolute right-0 top-0 z-[1] -translate-y-1/2 translate-x-1/2"
          />
        </div>
      </div>
    );
  }

  if (isText) {
    return (
      <button
        type="button"
        className={`cursor-pointer whitespace-nowrap border-0 bg-transparent p-0 text-sm font-medium text-[#FF621D] ${className}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleMessage();
        }}
      >
        {label ||
          t('detail.need_help_message_brand', {
            defaultValue: 'Need help? Message brand'
          })}
      </button>
    );
  }

  if (isModule) {
    return (
      <div
        className={`w-full flex items-center gap-3 rounded-2xl bg-white px-6 py-3 border border-[#E7E5E4] cursor-pointer relative ${className}`}
        onClick={handleMessage}
      >
        <div
          className="relative mr-1 w-10 h-10 rounded-xl bg-[#FFF0E6] flex items-center justify-center flex-shrink-0"
          data-testid="message-button-icon"
        >
          <MessageIcon className="w-5 h-5 text-[#FB6011]" />
          <UnreadCountBadge
            count={timUnreadCount}
            className="absolute right-0 top-0 z-[1] -translate-y-1/2 translate-x-1/2"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <div className="text-[#1C1917] text-sm font-semibold font-['DM_Sans'] leading-5">
            {t('detail.message_brand_btn', {
              defaultValue: 'Message brand'
            })}
          </div>
          <div className="text-[#79716B] text-xs font-normal font-['DM_Sans'] leading-4">
            {t('detail.message_brand_desc', {
              defaultValue: 'Chat about this deal'
            })}
          </div>
        </div>
        <RightArrowIcon className="w-4 h-4 text-[#A9A29D] flex-shrink-0" />
      </div>
    );
  }

  return (
    <div
      className={`h-[40px] flex items-center justify-center text-[#44403C] text-sm font-semibold gap-1 border rounded-md px-[14px] border-[#D7D3D0] cursor-pointer relative w-full md:w-auto ${className}`}
      onClick={handleMessage}
    >
      <div className="flex items-center gap-3">
        {showIcon && (
          <div
            className="relative mr-1 flex h-5 w-5 flex-shrink-0 items-center justify-center"
            data-message-slot="icon"
            data-testid="message-button-icon"
          >
            {icon ?? <MessageIcon className="h-5 w-5" />}
            <UnreadCountBadge
              count={timUnreadCount}
              className="absolute right-0 top-0 z-[1] -translate-y-1/2 translate-x-1/2"
            />
          </div>
        )}
        <div
          className="relative justify-start font-['DM_Sans'] leading-5"
          data-message-slot="label"
          data-testid="message-button-label"
        >
          <span>
            {t('detail.message_brand_btn', {
              defaultValue: 'Message brand'
            })}
          </span>
          {!showIcon && (
            <UnreadCountBadge
              count={timUnreadCount}
              className="absolute left-full top-0 z-[1] ml-1 -translate-y-1/2"
            />
          )}
        </div>
      </div>
    </div>
  );
}
